import React, { useEffect, useState } from "react";
import { BASE_URL } from "../../Config/api.config";
import useLocalStorage from "../../Hooks/useLocalStorage";
import AllEmployersData from "./AllEmployersData";

const EmployersPanel = () => {
  const [adminData] = useLocalStorage("adminData", "");
  const [employersData, setEmployersData] = useState({
    employers: [],
    filteredEmployers: [],
    pagination: null,
    loading: true,
    error: null,
  });

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    verified: "",
    date_from: "",
    date_to: "",
    sort_by: "date_created",
    sort_order: "DESC",
    page: 1,
    limit: 20,
  });

  const getAllEmployers = (currentFilters = filters) => {
    setEmployersData((prev) => ({ ...prev, loading: true, error: null }));

    // Build query string from filters
    const queryParams = new URLSearchParams();

    Object.keys(currentFilters).forEach((key) => {
      if (currentFilters[key]) {
        queryParams.append(key, currentFilters[key]);
      }
    });

    const url = `${BASE_URL}adm.employers${
      queryParams.toString() ? "?" + queryParams.toString() : ""
    }`;

    fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${adminData.uid}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data.status === "ok" && data.result && data.result.data) {
          setEmployersData({
            employers: data.result.data,
            filteredEmployers: data.result.data,
            pagination: data.result.pagination,
            filters: data.result.filters,
            systemSummary: data.result.system_summary,
            loading: false,
            error: null,
          });
        } else {
          throw new Error(data.message || "Invalid response format");
        }
      })
      .catch((error) => {
        console.error("Error fetching employers:", error);
        setEmployersData((prev) => ({
          ...prev,
          loading: false,
          error: error.message,
        }));
      });
  };

  const handleFilterChange = (newFilters) => {
    const updatedFilters = { ...filters, ...newFilters, page: 1 }; // Reset to page 1 on filter change
    setFilters(updatedFilters);
    getAllEmployers(updatedFilters);
  };

  const handlePageChange = (page) => {
    const updatedFilters = { ...filters, page };
    setFilters(updatedFilters);
    getAllEmployers(updatedFilters);
  };

  const handleSearch = (searchTerm) => {
    const updatedFilters = { ...filters, search: searchTerm, page: 1 };
    setFilters(updatedFilters);
    getAllEmployers(updatedFilters);
  };

  const handleStatusFilter = (status) => {
    const updatedFilters = { ...filters, status: status || "", page: 1 };
    setFilters(updatedFilters);
    getAllEmployers(updatedFilters);
  };

  const handleVerifiedFilter = (verified) => {
    const updatedFilters = { ...filters, verified: verified || "", page: 1 };
    setFilters(updatedFilters);
    getAllEmployers(updatedFilters);
  };

  const handleDateFilter = (dateFrom, dateTo) => {
    const updatedFilters = {
      ...filters,
      date_from: dateFrom || "",
      date_to: dateTo || "",
      page: 1,
    };
    setFilters(updatedFilters);
    getAllEmployers(updatedFilters);
  };

  const handleSort = (sortBy, sortOrder) => {
    const updatedFilters = {
      ...filters,
      sort_by: sortBy,
      sort_order: sortOrder,
      page: 1,
    };
    setFilters(updatedFilters);
    getAllEmployers(updatedFilters);
  };

  const handleLimitChange = (limit) => {
    const updatedFilters = { ...filters, limit, page: 1 };
    setFilters(updatedFilters);
    getAllEmployers(updatedFilters);
  };

  const refreshData = () => {
    getAllEmployers();
  };

  useEffect(() => {
    if (adminData && adminData.uid) {
      getAllEmployers();
    }
  }, [adminData]);

  return (
    <div className="py-12 px-4 sm:px-6 lg:px-8 w-full max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Employers Management
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          View and manage all employer accounts. Total employers:{" "}
          {employersData.pagination?.total_items || 0}
        </p>
      </div>

      {employersData.loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : employersData.error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading employers
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{employersData.error}</p>
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={refreshData}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <AllEmployersData
          employersData={employersData}
          filters={filters}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onStatusFilter={handleStatusFilter}
          onVerifiedFilter={handleVerifiedFilter}
          onDateFilter={handleDateFilter}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          onRefresh={refreshData}
        />
      )}
    </div>
  );
};

export default EmployersPanel;
